
import React, { useContext, useEffect, useRef, useState } from 'react';
import { TimerContext } from '../contexts/TimerContext';
import { TaskContext } from '../contexts/TaskContext';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { TextField, Paper, Box, Typography, Button, Chip, List, ListItem, ListItemText, Collapse, IconButton, ListItemButton, Menu, MenuItem, ListItemIcon } from '@mui/material';
import { PlayArrow, Pause, Stop, PlayCircleOutline, ExpandMore, ExpandLess, Add, DragIndicator, Delete, CheckCircle, RemoveCircle } from '@mui/icons-material';
import beepSound from '../sounds/beep.mp3';

const PomodoroPage = () => {
  const {
    mode,
    setMode,
    time,
    pomodoroDuration,
    setPomodoroDuration,
    isActive,
    isPaused,
    startPomodoroTimer,
    stopTimer,
    pauseTimer,
    resumeTimer,
  } = useContext(TimerContext);
  const { tasks, addTask, reorderTasks, selectTask, selectedTask, deleteTask, updateTaskStatus } = useContext(TaskContext);
  const audioRef = useRef(null);
  const defaultTitleRef = useRef('Pomodoro');

  useEffect(() => {
    setMode('pomodoro');
    return () => setMode('free');
  }, [setMode]);

  const [hasTimerRun, setHasTimerRun] = useState(false);
  const [taskListOpen, setTaskListOpen] = useState(false);
  const [completedTasksOpen, setCompletedTasksOpen] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [contextMenu, setContextMenu] = useState({ open: false, x: 0, y: 0, task: null });

  useEffect(() => {
    if (isActive && !isPaused) {
      setHasTimerRun(true);
    }
  }, [isActive, isPaused]);

  useEffect(() => {
    if (!isActive && !isPaused && time === 0 && mode === 'pomodoro' && hasTimerRun) {
      audioRef.current?.play();
      setHasTimerRun(false);
    }
  }, [time, isActive, isPaused, mode, hasTimerRun]);

  // Update the page title with remaining time while counting down
  useEffect(() => {
    if (mode !== 'pomodoro') return;

    const defaultTitle = defaultTitleRef.current;

    if ((isActive || isPaused) && time > 0) {
      // const prefix = isPaused ? '⏸' : '⏳';
      document.title = `${formatTime(time)} — Pomodoro`;
    } else {
      // Restore the default title when not running/paused
      document.title = defaultTitle;
    }

    return () => {
      // Ensure title is restored if this effect re-runs or component unmounts
      if (!isActive && !isPaused) {
        document.title = defaultTitle;
      }
    };
  }, [time, isActive, isPaused, mode]);

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleDurationChange = (e) => {
    const minutes = parseFloat(e.target.value) || 0;
    setPomodoroDuration(Math.round(minutes * 60));
  };

  const setQuickDuration = (seconds) => {
    setPomodoroDuration(seconds);
  };

  const handleAddTask = () => {
    if (newTaskName.trim()) {
      addTask(newTaskName.trim());
      setNewTaskName('');
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleAddTask();
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    reorderTasks(result.source.index, result.destination.index);
  };

  const handleRightClick = (event, task) => {
    event.preventDefault();
    setContextMenu({
      open: true,
      x: event.clientX,
      y: event.clientY,
      task: task
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu({ open: false, x: 0, y: 0, task: null });
  };

  const handleStatusChange = (status) => {
    if (contextMenu.task) {
      updateTaskStatus(contextMenu.task.id, status);
    }
    handleCloseContextMenu();
  };

  const handleDeleteTask = () => {
    if (contextMenu.task) {
      deleteTask(contextMenu.task.id);
    }
    handleCloseContextMenu();
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      <Paper sx={{ p: 4, textAlign: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Pomodoro Timer
        </Typography>
        
        <TextField
          label="Duration (minutes)"
          type="number"
          value={pomodoroDuration / 60}
          onChange={handleDurationChange}
          disabled={isActive || isPaused}
          sx={{ mb: 2, minWidth: 250 }}
          inputProps={{ min: 0.1, max: 120, step: 0.1 }}
        />
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Quick Duration:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => setQuickDuration(30)}
              disabled={isActive || isPaused}
              sx={{ borderRadius: 20, minWidth: 'auto', px: 2 }}
            >
              30s
            </Button>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => setQuickDuration(60)}
              disabled={isActive || isPaused}
              sx={{ borderRadius: 20, minWidth: 'auto', px: 2 }}
            >
              1m
            </Button>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => setQuickDuration(300)}
              disabled={isActive || isPaused}
              sx={{ borderRadius: 20, minWidth: 'auto', px: 2 }}
            >
              5m
            </Button>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => setQuickDuration(600)}
              disabled={isActive || isPaused}
              sx={{ borderRadius: 20, minWidth: 'auto', px: 2 }}
            >
              10m
            </Button>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => setQuickDuration(900)}
              disabled={isActive || isPaused}
              sx={{ borderRadius: 20, minWidth: 'auto', px: 2 }}
            >
              15m
            </Button>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => setQuickDuration(1500)}
              disabled={isActive || isPaused}
              sx={{ borderRadius: 20, minWidth: 'auto', px: 2 }}
            >
              25m
            </Button>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => setQuickDuration(1800)}
              disabled={isActive || isPaused}
              sx={{ borderRadius: 20, minWidth: 'auto', px: 2 }}
            >
              30m
            </Button>
          </Box>
        </Box>
        
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="h1" 
            component="div"
            sx={{ 
              fontFamily: 'monospace',
              fontSize: { xs: '3rem', sm: '5rem' },
              fontWeight: 'bold',
              color: isActive && !isPaused ? 'primary.main' : 'text.primary'
            }}
          >
            {formatTime(time)}
          </Typography>
          
          {isActive && (
            <Chip 
              label={isPaused ? 'Paused' : 'Running'} 
              color={isPaused ? 'warning' : 'success'} 
              variant="outlined"
              sx={{ mt: 1 }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          {!isActive && !isPaused ? (
            <Button 
              onClick={startPomodoroTimer} 
              variant="contained" 
              color="primary"
              size="large"
              startIcon={<PlayArrow />}
              sx={{ minWidth: 120 }}
            >
              Start
            </Button>
          ) : (
            <>
              {isPaused ? (
                <Button 
                  onClick={resumeTimer} 
                  variant="contained" 
                  color="primary"
                  size="large"
                  startIcon={<PlayCircleOutline />}
                  sx={{ minWidth: 120 }}
                >
                  Resume
                </Button>
              ) : (
                <Button 
                  onClick={pauseTimer} 
                  variant="contained" 
                  color="warning"
                  size="large"
                  startIcon={<Pause />}
                  sx={{ minWidth: 120 }}
                >
                  Pause
                </Button>
              )}
              <Button 
                onClick={stopTimer} 
                variant="contained" 
                color="error"
                size="large"
                startIcon={<Stop />}
                sx={{ minWidth: 120 }}
              >
                Stop
              </Button>
            </>
          )}
        </Box>
      </Paper>
      
      {/* Task List Card - Same functionality as Timer page */}
      <Paper sx={{ mb: 3 }}>
        <ListItemButton onClick={() => setTaskListOpen(!taskListOpen)} sx={{ py: 2 }}>
          <ListItemText 
            primary="Tasks" 
            secondary={`${tasks.filter(t => !t.completed && !t.removed).length} active tasks`}
            primaryTypographyProps={{ variant: 'h6', fontWeight: 'medium' }}
          />
          <IconButton edge="end" size="small">
            {taskListOpen ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </ListItemButton>
        
        <Collapse in={taskListOpen} timeout="auto" unmountOnExit>
          <Box sx={{ p: 2, pt: 0 }}>
            {/* Add Task Section */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3, mt: 1 }}>
              <TextField
                label="Add new task"
                value={newTaskName}
                onChange={(e) => setNewTaskName(e.target.value)}
                onKeyPress={handleKeyPress}
                size="small"
                fullWidth
              />
              <Button 
                onClick={handleAddTask} 
                variant="contained" 
                startIcon={<Add />}
                disabled={!newTaskName.trim()}
              >
                Add
              </Button>
            </Box>

            {/* Active Tasks with Drag & Drop */}
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="tasks">
                {(provided, snapshot) => {
                  const activeTasks = tasks
                    .filter(task => !task.completed && !task.removed)
                    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

                  return (
                    <List 
                      {...provided.droppableProps} 
                      ref={provided.innerRef}
                      sx={{ 
                        bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'inherit',
                        minHeight: 200,
                        borderRadius: 1
                      }}
                    >
                      {activeTasks.map((task, index) => (
                        <Draggable key={task.id} draggableId={task.id} index={index}>
                          {(provided, snapshot) => (
                            <ListItem
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              disablePadding
                              sx={{ 
                                mb: 1,
                                borderRadius: 1,
                                bgcolor: selectedTask?.id === task.id ? 'primary.light' : 'background.paper',
                                '&:hover': { bgcolor: 'action.hover' },
                                boxShadow: snapshot.isDragging ? 2 : 0
                              }}
                            >
                              <ListItemButton 
                                onClick={() => selectTask(task.id)}
                                onContextMenu={(e) => handleRightClick(e, task)}
                                sx={{ pl: 1 }}
                              >
                                <Box 
                                  {...provided.dragHandleProps}
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    mr: 1,
                                    color: 'text.secondary'
                                  }}
                                >
                                  <DragIndicator />
                                </Box>
                                <ListItemText 
                                  primary={task.name}
                                  sx={{ 
                                    color: selectedTask?.id === task.id ? 'primary.contrastText' : 'inherit'
                                  }}
                                />
                                {selectedTask?.id === task.id && (
                                  <Chip 
                                    label="Current" 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined"
                                  />
                                )}
                              </ListItemButton>
                            </ListItem>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {activeTasks.length === 0 && (
                        <ListItem>
                          <ListItemText 
                            primary="No active tasks" 
                            primaryTypographyProps={{ 
                              color: 'textSecondary',
                              align: 'center',
                              fontStyle: 'italic'
                            }}
                          />
                        </ListItem>
                      )}
                    </List>
                  );
                }}
              </Droppable>
            </DragDropContext>

            {/* Completed Tasks Section - Collapsible */}
            {(() => {
              const completedTasks = tasks.filter(task => task.completed && !task.removed);
              return completedTasks.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Box 
                    onClick={() => setCompletedTasksOpen(!completedTasksOpen)}
                    sx={{ 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      py: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                      borderRadius: 1,
                      px: 1
                    }}
                  >
                    <Typography variant="subtitle2" color="textSecondary" sx={{ flexGrow: 1 }}>
                      Completed ({completedTasks.length})
                    </Typography>
                    <IconButton size="small">
                      {completedTasksOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                  </Box>
                  
                  <Collapse in={completedTasksOpen} timeout="auto" unmountOnExit>
                    <List dense>
                      {completedTasks.slice(-5).map((task) => (
                        <ListItem key={task.id} sx={{ pl: 0 }}>
                          <ListItemText 
                            primary={task.name}
                            primaryTypographyProps={{ 
                              sx: { textDecoration: 'line-through', color: 'text.secondary' }
                            }}
                          />
                          <Chip label="Done" size="small" color="success" variant="outlined" />
                        </ListItem>
                      ))}
                      {completedTasks.length > 5 && (
                        <ListItem sx={{ pl: 0 }}>
                          <ListItemText 
                            primary={`... and ${completedTasks.length - 5} more completed tasks`}
                            primaryTypographyProps={{ 
                              color: 'textSecondary',
                              fontStyle: 'italic'
                            }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Collapse>
                </Box>
              );
            })()}
          </Box>
        </Collapse>
      </Paper>
      
      {/* Context Menu - Same as Timer page */}
      <Menu
        open={contextMenu.open}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu.open
            ? { top: contextMenu.y, left: contextMenu.x }
            : undefined
        }
      >
        <MenuItem onClick={() => handleStatusChange('New')}>
          <ListItemIcon>
            <RemoveCircle fontSize="small" />
          </ListItemIcon>
          Mark as New
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('Started')}>
          <ListItemIcon>
            <PlayArrow fontSize="small" />
          </ListItemIcon>
          Mark as Started
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('Completed')}>
          <ListItemIcon>
            <CheckCircle fontSize="small" />
          </ListItemIcon>
          Mark as Completed
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('Removed')}>
          <ListItemIcon>
            <RemoveCircle fontSize="small" />
          </ListItemIcon>
          Mark as Removed
        </MenuItem>
        <MenuItem onClick={handleDeleteTask} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          Delete Task
        </MenuItem>
      </Menu>

      <audio ref={audioRef} src={beepSound} />
    </Box>
  );
};

export default PomodoroPage;
